import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation, useHistory } from "react-router-dom";
import { Constants } from "../../endPoints";
import Checkbox from "@mui/material/Checkbox";

export default function Shopsidebar({ onFilterChange }) {
    // ---------- State ----------
    const [countries, setCountries] = useState([]);
    const [selections, setSelections] = useState([]);
    const [categories, setCategories] = useState([]);
    const [shopFor, setShopFor] = useState([]);
    const [ringStyle, setRingStyle] = useState([]);
    const [pearlColor, setPearlColor] = useState([]);
    const [pearlType, setPearlType] = useState([]);
    const [diamondColor, setDiamondColor] = useState([]);
    const [clarity, setClarity] = useState([]);
    const [stoneColor, setStoneColor] = useState([]);

    // Selected filters
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [selectedSelections, setSelectedSelections] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedShopFor, setSelectedShopFor] = useState([]);
    const [selectedRingStyle, setSelectedRingStyle] = useState([]);
    const [selectedPearlColor, setSelectedPearlColor] = useState([]);
    const [selectedPearlType, setSelectedPearlType] = useState([]);
    const [selectedDiamondColor, setSelectedDiamondColor] = useState([]);
    const [selectedClarity, setSelectedClarity] = useState([]);
    const [selectedStoneColor, setSelectedStoneColor] = useState([]);

    // Visibility toggles
    const [isCountryVisible, setIsCountryVisible] = useState(true);
    const [isSelectionVisible, setIsSelectionVisible] = useState(true);
    const [isCategoryVisible, setIsCategoryVisible] = useState(true);
    const [isShopForVisible, setIsShopForVisible] = useState(true);
    const [isRingStyleVisible, setIsRingStyleVisible] = useState(true);
    const [isPearlColorVisible, setIsPearlColorVisible] = useState(true);
    const [isPearlTypeVisible, setIsPearlTypeVisible] = useState(true);
    const [isDiamondColorVisible, setIsDiamondColorVisible] = useState(true);
    const [isClarityVisible, setIsClarityVisible] = useState(true);
    const [isStoneColorVisible, setIsStoneColorVisible] = useState(true);

    const location = useLocation();
    const history = useHistory();

    const queryParams = new URLSearchParams(location.search);
    const idCountry = queryParams.get("country") || "";
    const idSelection = queryParams.get("selection") || "";
    const idCategory = queryParams.get("category") || "";
    const idShopFor = queryParams.get("shopFor") || "";
    const idRingStyle = queryParams.get("ringStyle") || "";

    // ---------- Initialize selected filters from URL ----------
    useEffect(() => {
        if (idCountry) setSelectedCountries(idCountry.split(","));
        if (idSelection) setSelectedSelections(idSelection.split(","));
        if (idCategory) setSelectedCategory(idCategory);
        if (idShopFor) setSelectedShopFor(idShopFor.split(","));
        if (idRingStyle) setSelectedRingStyle(idRingStyle.split(","));
    }, [idCountry, idSelection, idCategory, idShopFor, idRingStyle]);

    // ---------- Fetch Countries ----------
    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const res = await axios.get(`${Constants.listCounty}`);
                let list = [];
                if (Array.isArray(res.data)) list = res.data;
                else if (Array.isArray(res.data.data)) list = res.data.data;
                else if (Array.isArray(res.data.list)) list = res.data.list;
                else if (res.data.data?.list && Array.isArray(res.data.data.list))
                    list = res.data.data.list;
                setCountries(list);
            } catch (err) {
                console.error("Error fetching countries:", err);
            }
        };
        fetchCountries();
    }, []);

    // ---------- Fetch Selections ----------
    useEffect(() => {
        const fetchSelections = async () => {
            try {
                const res = await axios.get(Constants.listSelection);
                setSelections(res.data.list || []);
            } catch (err) {
                console.error("Error fetching selections:", err);
            }
        };
        fetchSelections();
    }, []);

    // ---------- Fetch Selected Main Selections ----------
    useEffect(() => {
        const fetchSelectedMainSelections = async () => {
            if (!idSelection) return;
            try {
                const res = await axios.get(
                    `${Constants.getSelectionById}/${idSelection}`
                );

                if (res.data.status === 1 && Array.isArray(res.data.list)) {
                    const ids = res.data.list.map((item) => item.id.toString());
                    setSelectedSelections(ids);
                } else if (res.data.status === 1 && res.data.data) {
                    setSelectedSelections([res.data.data.id.toString()]);
                }
            } catch (err) {
                console.error("Error fetching main selections:", err);
            }
        };

        fetchSelectedMainSelections();
    }, [idSelection]);

    // ---------- Fetch Categories ----------
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const res = await axios.get(
                    `${Constants.listCategory}?country=${selectedCountries.join(
                        ","
                    )}&selection=${selectedSelections.join(",")}`
                );
                setCategories(res.data.list || []);
            } catch (err) {
                console.error("Error fetching categories:", err);
            }
        };
        fetchCategories();
    }, [selectedCountries, selectedSelections]);

    // ---------- Fetch Static Filters ----------
    useEffect(() => {
        const fetchFilters = async () => {
            try {
                const [
                    shopRes,
                    ringRes,
                    pearlCRes,
                    pearlTRes,
                    diamondRes,
                    clarityRes,
                    stoneRes,
                ] = await Promise.all([
                    axios.get(`${Constants.listShopFor}`),
                    axios.get(`${Constants.listRingStyle}`),
                    axios.get(`${Constants.listPearlColor}`),
                    axios.get(`${Constants.listPearlType}`),
                    axios.get(`${Constants.listDiamondColor}`),
                    axios.get(`${Constants.listClarity}`),
                    axios.get(`${Constants.listStoneColor}`),
                ]);

                setShopFor(shopRes.data.list || []);
                setRingStyle(ringRes.data.list || []);
                setPearlColor(pearlCRes.data.list || []);
                setPearlType(pearlTRes.data.list || []);
                setDiamondColor(diamondRes.data.list || []);
                setClarity(clarityRes.data.list || []);
                setStoneColor(stoneRes.data.list || []);
            } catch (err) {
                console.error("Error fetching filters:", err);
            }
        };
        fetchFilters();
    }, []);

    // ---------- Auto-select "Shop For" based on Main Selection or Category ----------
    useEffect(() => {
        const autoSelectShopFor = async () => {
            if (selectedSelections.length === 0 && !selectedCategory) return;

            try {
                const res = await axios.get(
                    `${Constants.listProduct}?page=1&limit=100&idSelection=${selectedSelections.join(
                        ","
                    )}&idCategory=${selectedCategory || ""}`
                );

                if (res.data.status === 1 && Array.isArray(res.data.list)) {
                    const shopForIds = [
                        ...new Set(
                            res.data.list
                                .map((item) => item.idShopFor)
                                .filter((id) => id !== null && id !== undefined)
                                .map((id) => id.toString())
                        ),
                    ];

                    if (shopForIds.length > 0) {
                        // 🧠 Merge with already selected shopFor, not overwrite
                        setSelectedShopFor((prev) => {
                            const merged = [...new Set([...prev, ...shopForIds])];

                            // 🧩 Only update URL if changed
                            const prevSorted = [...prev].sort().join(",");
                            const mergedSorted = [...merged].sort().join(",");
                            if (prevSorted !== mergedSorted) {
                                updateURL({ shopFor: merged });
                            }

                            return merged;
                        });
                    }
                }
            } catch (error) {
                console.error("Error auto-selecting shopFor:", error);
            }
        };

        autoSelectShopFor();
        // ✅ Do NOT include location.search here to avoid infinite re-renders
    }, [selectedSelections, selectedCategory]);


    // ---------- URL + Checkbox Handling ----------
    const updateURL = (newParams) => {
        const currentParams = new URLSearchParams(location.search);

        const allParams = {
            country: selectedCountries,
            selection: selectedSelections,
            category: selectedCategory ? [selectedCategory] : [],
            shopFor: selectedShopFor,
            ringStyle: selectedRingStyle,
            pearlColor: selectedPearlColor,
            pearlType: selectedPearlType,
            diamondColor: selectedDiamondColor,
            clarity: selectedClarity,
            stoneColor: selectedStoneColor,
            ...newParams,
        };

        Object.entries(allParams).forEach(([key, values]) => {
            if (!values || values.length === 0) currentParams.delete(key);
            else currentParams.set(key, [...new Set(values)].join(","));
        });

        history.replace({ search: currentParams.toString() });
    };
    onFilterChange && onFilterChange({
        countries: selectedCountries.length > 0,
        selections: selectedSelections.length > 0,
        category: !!selectedCategory,
        shopFor: selectedShopFor.length > 0
    });
    const handleCheckboxChange = (setState, value, key) => {
        const val = value.toString();
        setState((prev) => {
            const updated = prev.includes(val)
                ? prev.filter((v) => v !== val)
                : [...prev, val];
            updateURL({ [key]: updated });
            onFilterChange && onFilterChange({
                countries: key === "country" ? updated.length > 0 : selectedCountries.length > 0,
                selections: key === "selection" ? updated.length > 0 : selectedSelections.length > 0,
                category: !!selectedCategory,
                shopFor: key === "shopFor" ? updated.length > 0 : selectedShopFor.length > 0
            });
            return updated;
        });
    };

    const handleCategoryChange = (categoryId) => {
        setSelectedCategory(categoryId);
        updateURL({ category: [categoryId] });
    };

    // ---------------- Render ----------------
    return (
        <>
            {/* COUNTRY */}
            <div className="border mb-4">
                <h2
                    className="px-4 py-3 bg-gray-100 uppercase font-semibold tracking-wider cursor-pointer"
                    onClick={() => setIsCountryVisible(!isCountryVisible)}
                >
                    Country
                </h2>
                {isCountryVisible && (
                    <div className="max-h-72 overflow-auto p-2 scroll-light">
                        {countries.map((c) => (
                            <label key={c.id} className="flex gap-3 text-left items-center">
                                <Checkbox
                                    checked={selectedCountries.includes(c.id.toString())}
                                    onChange={() =>
                                        handleCheckboxChange(setSelectedCountries, c.id, "country")
                                    }
                                    sx={{ "& .MuiSvgIcon-root": { fontSize: 19 } }}
                                />
                                <div className="flex items-center gap-2">
                                    {c.country_symbols && (
                                        <img
                                            src={c.country_symbols}
                                            alt={c.country}
                                            style={{ width: 20, height: 14 }}
                                        />
                                    )}
                                    {c.country}
                                </div>
                            </label>
                        ))}
                    </div>
                )}
            </div>

            {/* MAIN SELECTION */}
            <div className="border mb-4">
                <h2
                    className="px-4 py-3 bg-gray-100 uppercase font-semibold tracking-wider cursor-pointer"
                    onClick={() => setIsSelectionVisible(!isSelectionVisible)}
                >
                    Main Selection
                </h2>
                {isSelectionVisible && (
                    <div className="max-h-72 overflow-auto p-2 scroll-light">
                        {selections.map((item) => (
                            <label key={item.id} className="flex gap-3 items-center">
                                <Checkbox
                                    checked={selectedSelections.includes(item.id.toString())}
                                    onChange={() =>
                                        handleCheckboxChange(
                                            setSelectedSelections,
                                            item.id,
                                            "selection"
                                        )
                                    }
                                    sx={{ "& .MuiSvgIcon-root": { fontSize: 19 } }}
                                />
                                <div className="flex items-center gap-2">
                                    {item.image && (
                                        <img
                                            src={`${Constants.imageBaseURL}${item.image}`}
                                            alt={item.name}
                                            style={{
                                                width: 25,
                                                height: 25,
                                                objectFit: "cover",
                                                borderRadius: "4px",
                                            }}
                                        />
                                    )}
                                    {item.name}
                                </div>
                            </label>
                        ))}
                    </div>
                )}
            </div>

            {/* CATEGORY */}
            <div className="border mb-4">
                <h2
                    className="px-4 py-3 bg-gray-100 uppercase font-semibold tracking-wider cursor-pointer"
                    onClick={() => setIsCategoryVisible(!isCategoryVisible)}
                >
                    Category
                </h2>
                {isCategoryVisible && (
                    <div className="max-h-72 overflow-auto p-2 scroll-light">
                        {categories.map((item) => (
                            <label key={item.id} className="flex gap-3 items-center">
                                <Checkbox
                                    checked={selectedCategory === item.id.toString()}
                                    onChange={() => handleCategoryChange(item.id.toString())}
                                    sx={{ "& .MuiSvgIcon-root": { fontSize: 19 } }}
                                />
                                <div className="flex items-center gap-2">
                                    {item.image && (
                                        <img
                                            src={`${Constants.imageBaseURL}${item.image}`}
                                            alt={item.name}
                                            style={{
                                                width: 25,
                                                height: 25,
                                                objectFit: "cover",
                                                borderRadius: "4px",
                                            }}
                                        />
                                    )}
                                    {item.name}
                                </div>
                            </label>
                        ))}
                    </div>
                )}
            </div>

            {/* OTHER FILTERS */}
            <FilterSection
                title="Shop For"
                visible={isShopForVisible}
                toggle={() => setIsShopForVisible(!isShopForVisible)}
                data={shopFor}
                selected={selectedShopFor}
                setSelected={setSelectedShopFor}
                param="shopFor"
            />
            {/* <FilterSection
        title="Ring Style"
        visible={isRingStyleVisible}
        toggle={() => setIsRingStyleVisible(!isRingStyleVisible)}
        data={ringStyle}
        selected={selectedRingStyle}
        setSelected={setSelectedRingStyle}
        param="ringStyle"
      />
      <FilterSection
        title="Pearl Color"
        visible={isPearlColorVisible}
        toggle={() => setIsPearlColorVisible(!isPearlColorVisible)}
        data={pearlColor}
        selected={selectedPearlColor}
        setSelected={setSelectedPearlColor}
        param="pearlColor"
      />
      <FilterSection
        title="Pearl Type"
        visible={isPearlTypeVisible}
        toggle={() => setIsPearlTypeVisible(!isPearlTypeVisible)}
        data={pearlType}
        selected={selectedPearlType}
        setSelected={setSelectedPearlType}
        param="pearlType"
      />
      <FilterSection
        title="Diamond Color"
        visible={isDiamondColorVisible}
        toggle={() => setIsDiamondColorVisible(!isDiamondColorVisible)}
        data={diamondColor}
        selected={selectedDiamondColor}
        setSelected={setSelectedDiamondColor}
        param="diamondColor"
      />
      <FilterSection
        title="Clarity"
        visible={isClarityVisible}
        toggle={() => setIsClarityVisible(!isClarityVisible)}
        data={clarity}
        selected={selectedClarity}
        setSelected={setSelectedClarity}
        param="clarity"
      />
      <FilterSection
        title="Stone Color"
        visible={isStoneColorVisible}
        toggle={() => setIsStoneColorVisible(!isStoneColorVisible)}
        data={stoneColor}
        selected={selectedStoneColor}
        setSelected={setSelectedStoneColor}
        param="stoneColor"
      /> */}
        </>
    );
}

// ---------- Reusable Filter Section ----------
const FilterSection = ({
    title,
    visible,
    toggle,
    data,
    selected,
    setSelected,
    param,
}) => (
    <div className="border mb-4">
        <h2
            className="px-4 py-3 bg-gray-100 uppercase font-semibold tracking-wider cursor-pointer"
            onClick={toggle}
        >
            {title}
        </h2>
        {visible && (
            <div className="max-h-72 overflow-auto p-2 scroll-light">
                {data.map((item) => (
                    <label key={item.id} className="flex gap-3 items-center">
                        <Checkbox
                            checked={selected.includes(item.id.toString())}
                            onChange={() =>
                                setSelected((prev) => {
                                    const val = item.id.toString();
                                    const updated = prev.includes(val)
                                        ? prev.filter((v) => v !== val)
                                        : [...prev, val];
                                    const params = new URLSearchParams(window.location.search);
                                    params.set(param, [...new Set(updated)].join(","));
                                    window.history.replaceState({}, "", `?${params.toString()}`);
                                    return updated;
                                })
                            }
                            sx={{ "& .MuiSvgIcon-root": { fontSize: 19 } }}
                        />
                        {item.name}
                    </label>
                ))}
            </div>
        )}
    </div>
);
